import React from 'react';
import { Star } from 'lucide-react';
import type { ResourceType } from '@cotera/api';
import { useIsFavorite } from './use-favorites';

/**
 * The star a favorited list row wears beside its name.
 *
 * `ochre` — the palette's earthy amber, the same ramp triggers are drawn in —
 * rather than Tailwind's stock `amber-500`, which is a full-chroma yellow that
 * no other mark in this warm, low-chroma theme reaches. The 500 stop is the one
 * the resource palette uses for its accent dots, so the star sits at the same
 * weight as the other coloured marks in a row.
 *
 * Drawn only when the row is starred: the toggle is one click away in the row's
 * `⋯` menu, and a hollow star on every row would put hundreds of inert marks on
 * a page to flag the handful that are on.
 *
 * A component rather than an inline `useIsFavorite` at each call site, so the
 * hook stays out of rows that have no resource behind them — the Library's
 * virtual "Chats" summary row — where it could not be called conditionally.
 */
export const FavoriteRowMarker: React.FC<{
  type: ResourceType;
  id: string;
}> = ({ type, id }) => {
  const favorited = useIsFavorite(type, id);
  if (!favorited) {
    return null;
  }
  return (
    <Star
      className="h-3.5 w-3.5 shrink-0 fill-current text-ochre-500"
      aria-label="Favorited"
    />
  );
};
